(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  if (!global.NewHomeSource.Helpers ||
    !global.NewHomeSource.Helpers.Array) {
    return;
  }

  const arrayHelper = global.NewHomeSource.Helpers.Array;

  class ScrollTarget {
    /**
     * Initializes a new instance of the class.
     * Sets the initial state to null.
     */
    constructor() {
      this.state = null;
    }

    /**
     * Initializes the object with the provided state.
     * @param {Object} state - The state object to initialize with.
     */
    initialize(state) {
      this.state = state;
    }

    /**
     * Calculates the appropriate scroll target based on the current target, direction, and looping state.
     *
     * @param {number} target - The current scroll target index or position.
     * @param {number} [direction] - The scroll direction (positive for forward, negative for backward). Optional.
     * @returns {number} The calculated scroll target index or position.
     */
    shortcut(target, direction) {
      const contentSize = this.state.get('contentSize');
      const loop = this.state.get('loop');
      const targets = [target, target + contentSize, target - contentSize];

      if (!loop) {
        return target;
      }
      if (!direction) {
        return ScrollTarget.minDistance(targets);
      }

      const matchingTargets = targets.filter((t) => Math.sign(t) === direction);

      if (matchingTargets.length) {
        return ScrollTarget.minDistance(matchingTargets);
      }

      return arrayHelper.arrayLast(targets) - contentSize;
    }

    /**
     * Finds the closest scroll snap index and the adjusted distance for a given target position.
     *
     * @param {number} target - The target scroll position to snap to.
     * @returns {{ index: number, distance: number }} An object containing the index of the
     * closest snap point and the adjusted distance.
     */
    findTargetSnap(target) {
      const loop = this.state.get('loop');
      const limit = this.state.get('scrollLimit');
      const scrollSnaps = this.state.get('scrollSnaps');
      const distance = loop ? limit.removeOffset(target) : limit.constrain(target);

      const ascDiffsToSnaps = scrollSnaps.map((snap, index) => ({
        diff: this.shortcut(snap - distance, 0),
        index
      })).sort((d1, d2) => Math.abs(d1.diff) - Math.abs(d2.diff));

      const index = ascDiffsToSnaps[0].index;

      return {
        index,
        distance
      };
    }

    /**
     * Calculates the scroll distance required to reach a specific snap index.
     *
     * @param {number} index - The index of the target scroll snap.
     * @param {number} direction - The direction in which to scroll (e.g., 1 for forward, -1 for backward).
     * @returns {{index: number, distance: number}} An object containing the target index and the
     * calculated scroll distance.
     */
    byIndex(index, direction) {
      const scrollSnaps = this.state.get('scrollSnaps');
      const target = this.state.get('target');
      const diffToSnap = scrollSnaps[index] - target.get();
      const distance = this.shortcut(diffToSnap, direction);
      return {
        index,
        distance
      };
    }

    /**
     * Calculates the target index and distance to scroll based on a given distance and snap option.
     *
     * @param {number} distance - The distance to scroll from the current target.
     * @param {boolean} snap - Whether to snap to the nearest scroll snap point.
     * @returns {{ index: number, distance: number }} An object containing the target index and the
     * calculated scroll distance.
     */
    byDistance(distance, snap) {
      const targetState = this.state.get('target');
      const loop = this.state.get('loop');
      const limit = this.state.get('scrollLimit');
      const scrollSnaps = this.state.get('scrollSnaps');
      const target = targetState.get() + distance;
      const result = this.findTargetSnap(target);
      const index = result.index;
      const targetSnapDistance = result.distance;
      const reachedBound = !loop && limit.reachedAny(target);

      if (!snap || reachedBound) {
        return {
          index,
          distance
        };
      }

      const diffToSnap = scrollSnaps[index] - targetSnapDistance;
      const snapDistance = distance + this.shortcut(diffToSnap, 0);

      return {
        index,
        distance: snapDistance
      };
    }

    /**
     * Returns the value from the array of distances that has the smallest absolute value.
     *
     * @param {number[]} distances - An array of numeric distances.
     * @returns {number} The distance with the smallest absolute value.
     */
    static minDistance(distances) {
      const copy = distances.concat();
      copy.sort((a, b) => Math.abs(a) - Math.abs(b));
      return copy[0];
    }
  }

  global.NewHomeSource.Carousel.ScrollTarget = ScrollTarget;
}(window));
